// Baselayer Results Explorer — Bankruptcies tab. Reads the SHARED docket add-on
// (ordered as either Litigations or Bankruptcies) and filters to is_bankruptcy
// dockets. Empty states: "Request Bankruptcy Information" (unordered) /
// "No Bankruptcies Found" (ordered, none matched).

import { useNavigate } from "react-router-dom";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  EmptyState,
  StatusBadge,
} from "@/design-system/components/ui";
import type { BackgroundCheckAddOnDto } from "@/api/backgroundChecks/addOnsApi";
import { AddOnEmptyState } from "../AddOnEmptyState";
import { findAddOn, isCompleted, formatLooseDate } from "../tabHelpers";
import { parseDockets } from "../parse";

interface BankruptciesTabProps {
  checkId: string;
  addOns: BackgroundCheckAddOnDto[] | undefined;
}

export function BankruptciesTab({ checkId, addOns }: BankruptciesTabProps) {
  const navigate = useNavigate();
  // Litigations + Bankruptcies share one docket add-on.
  const addOn = findAddOn(addOns, "Bankruptcies", "Litigations");

  if (!addOn || !isCompleted(addOn)) {
    return (
      <AddOnEmptyState
        checkId={checkId}
        addOnType="Bankruptcies"
        label="Bankruptcies"
        title="Request Bankruptcy Information"
        description="Order a docket search to see bankruptcy records for this subject."
        addOn={addOn}
      />
    );
  }

  const bankruptcies = parseDockets(addOn.rawResponseJson).filter(
    (d) => d.isBankruptcy,
  );

  if (bankruptcies.length === 0) {
    return (
      <EmptyState
        title="No Bankruptcies Found"
        description="No bankruptcy dockets matched this subject."
      />
    );
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>Status</TH>
          <TH>Filing date</TH>
          <TH>Name / docket #</TH>
          <TH>Court</TH>
          <TH>Chapter / type</TH>
        </TR>
      </THead>
      <TBody>
        {bankruptcies.map((d, i) => (
          <TR
            key={d.id ?? i}
            className="cursor-pointer"
            onClick={() =>
              d.id &&
              navigate(`/background-checks/${checkId}/litigations/${d.id}`)
            }
          >
            <TD>
              {d.status ? (
                <StatusBadge tone="info">{d.status}</StatusBadge>
              ) : (
                "—"
              )}
            </TD>
            <TD>{formatLooseDate(d.dateFiled)}</TD>
            <TD>
              <span className="block">{d.title ?? "—"}</span>
              {d.docketNumber && (
                <span className="block text-caption text-gray-mid">
                  {d.docketNumber}
                </span>
              )}
            </TD>
            <TD>{d.court ?? "—"}</TD>
            <TD>{d.bankruptcyType ?? d.caseType ?? "—"}</TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
